import type { SidebarProjectSnapshot } from "../SidebarProjectItem";
import {
  runtimeScopedSidebarKey,
  type SavedEnvironmentRuntimeIndex,
} from "../sidebarThreadRuntimeIdentity";

/** Resolve a project's representative and grouped members through runtime aliases. */
function memberKeys(project: SidebarProjectSnapshot, environments: SavedEnvironmentRuntimeIndex) {
  return [
    { environmentId: project.environmentId, projectId: project.id },
    ...(project.memberProjectRefs ?? []),
  ].map((member) => runtimeScopedSidebarKey(member.environmentId, member.projectId, environments));
}

/** Keep the live group identity while adding inventory members absent from that group. */
function mergeInventoryMembers(
  existing: SidebarProjectSnapshot,
  inventory: SidebarProjectSnapshot,
  environments: SavedEnvironmentRuntimeIndex,
  knownMembers: Set<string>,
): SidebarProjectSnapshot {
  const memberProjectRefs = [...(existing.memberProjectRefs ?? [])];
  for (const member of [
    { environmentId: inventory.environmentId, projectId: inventory.id },
    ...(inventory.memberProjectRefs ?? []),
  ]) {
    const key = runtimeScopedSidebarKey(member.environmentId, member.projectId, environments);
    if (knownMembers.has(key)) continue;
    knownMembers.add(key);
    memberProjectRefs.push(member);
  }
  return { ...existing, memberProjectRefs };
}

/** Add inventory-only groups to the live project list without duplicating connected members. */
export function mergeSidebarV2Projects(input: {
  readonly live: readonly SidebarProjectSnapshot[];
  readonly inventory: readonly SidebarProjectSnapshot[];
  readonly environments: SavedEnvironmentRuntimeIndex;
}): readonly SidebarProjectSnapshot[] {
  const projects = [...input.live];
  const knownMembers = new Set(
    input.live.flatMap((project) => memberKeys(project, input.environments)),
  );
  for (const project of input.inventory) {
    const keys = memberKeys(project, input.environments);
    if (keys.every((key) => knownMembers.has(key))) continue;
    const projectKeys = new Set(keys);
    const overlappingIndex = projects.findIndex((existing) =>
      memberKeys(existing, input.environments).some((key) => projectKeys.has(key)),
    );
    if (overlappingIndex >= 0) {
      const existing = projects[overlappingIndex]!;
      projects[overlappingIndex] = mergeInventoryMembers(
        existing,
        project,
        input.environments,
        knownMembers,
      );
      for (const key of keys) knownMembers.add(key);
      continue;
    }
    projects.push(project);
    for (const key of keys) knownMembers.add(key);
  }
  return projects;
}
